/** QuickRouter — Écran d'accueil : identité de suite, projets (les miens et partagés),
 *  création, import / export, partage ; écran d'accès refusé hors session. */
import { useEffect, useRef, useState } from 'react';
import { useProjectStore } from '../store/useProjectStore.js';
import { portalUrl } from '../api/client.js';
import ShareDialog from './ShareDialog.jsx';
import TemplatePreview from './TemplatePreview.jsx';

const ROLE_LABEL = { editor: 'contributeur', viewer: 'lecture seule' };

/** Cartes mères proposées à la création, et pour la VENTUNO Q ses formes de carte. */
const BOARDS = [
  { id: 'ventuno-q', name: 'Arduino VENTUNO Q', sub: 'Shields, HAT et porteuses dédiés' },
  { id: 'uno-shield', name: 'Shield UNO', sub: 'Arduino UNO R3, UNO Q' },
  { id: 'rpi-hat', name: 'HAT Raspberry Pi', sub: 'Connecteur 40 broches' },
  { id: 'libre', name: 'Carte libre', sub: 'Nœud ESP32, interface, alimentation…' },
];
const VQ_FORMES = [
  { forme: 0, name: 'Shield sur l’UNO', sub: 'Au-dessus, sur JANALOG + JDIGITAL' },
  { forme: 1, name: 'HAT sur le JHAT', sub: 'Au-dessus, connecteur 40 broches' },
  { forme: 2, name: 'Grande carte UNO + JHAT', sub: 'Au-dessus, sous le dissipateur' },
  { forme: 3, name: 'Porteuse UNO', sub: 'Dessous, JMISC + JMEDIA' },
  { forme: 4, name: 'Grande porteuse', sub: 'Dessous, JMISC + JMEDIA + JOMEGA' },
];

function Denied({ error }) {
  const portal = portalUrl() ?? 'https://portail.quick-apps.fr/';
  const noSession = error.status === 401;
  return (
    <div className="picker">
      <h1>Quick<span className="logo-sfx">·Router</span></h1>
      <div className="denied">
        {noSession
          ? <p>Connectez-vous au portail de la suite Quick-Apps, puis revenez : la session vaut pour toutes les applications.</p>
          : <p>{error.message}</p>}
        <a className="btn primary" href={portal}>{noSession ? 'Se connecter au portail' : 'Retour au portail'}</a>
      </div>
    </div>
  );
}

export default function ProjectPicker() {
  const me = useProjectStore((s) => s.me);
  const authError = useProjectStore((s) => s.authError);
  const loadMe = useProjectStore((s) => s.loadMe);
  const projects = useProjectStore((s) => s.projects);
  const loadProjects = useProjectStore((s) => s.loadProjects);
  const openProject = useProjectStore((s) => s.openProject);
  const createProject = useProjectStore((s) => s.createProject);
  const deleteProjectById = useProjectStore((s) => s.deleteProjectById);
  const [name, setName] = useState('');
  const [template, setTemplate] = useState('ventuno-q');
  const [forme, setForme] = useState(0);
  const [dims, setDims] = useState({ w: 80, h: 60 });
  const [empilage, setEmpilage] = useState(5);
  const [filter, setFilter] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [sharing, setSharing] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    loadMe().then((ok) => { if (ok) loadProjects().catch((e) => setError(e.message)); });
  }, [loadMe, loadProjects]);

  if (authError) return <Denied error={authError} />;

  const params = template === 'libre' ? dims : template === 'ventuno-q' ? { forme, empilage } : {};

  const create = async () => {
    if (!name.trim()) return;
    try { await createProject(name.trim(), template, params); } catch (e) { setError(e.message); }
  };

  const exportProject = async (p) => {
    setError('');
    try {
      const res = await fetch(`/api/projects/${p.id}/export`);
      if (!res.ok) throw new Error('Export impossible');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${p.name.replace(/[^\w.-]+/g, '_') || 'projet'}.quickrouter.json`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { setError(e.message); }
  };

  const importFile = async (file) => {
    setError('');
    setBusy(`Import de ${file.name}…`);
    try {
      let json;
      try { json = JSON.parse(await file.text()); } catch { throw new Error('Fichier illisible : JSON attendu (.quickrouter.json)'); }
      const res = await fetch('/api/projects/import', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(json) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.details ? `${data.error} — ${data.details.slice(0, 3).join(' ; ')}` : (data.error ?? 'Import impossible'));
      await loadProjects();
      setBusy('');
      if (window.confirm(`Projet « ${data.name} » importé. L'ouvrir maintenant ?`)) await openProject(data.id);
    } catch (e) {
      setBusy('');
      setError(e.message);
    }
  };

  const portal = portalUrl();
  const mine = projects.filter((p) => p.mine);
  const shared = projects.filter((p) => !p.mine);

  const row = (p) => {
    const nShared = (p.sharedWith?.length ?? 0) + (p.viewers?.length ?? 0);
    return (
      <div key={p.id} className="item" onClick={() => openProject(p.id).catch((e) => setError(e.message))}>
        <span className="grow">
          {p.name}
          {!p.mine && <span className="badge" title={`Partagé par ${p.owner}`}>{p.owner} · {ROLE_LABEL[p.role] ?? p.role}</span>}
          {p.mine && nShared > 0 && <span className="badge" title={[...(p.sharedWith ?? []), ...(p.viewers ?? [])].join(', ')}>partagé ×{nShared}</span>}
        </span>
        <span className="meta">{p.components} composant{p.components > 1 ? 's' : ''} — {new Date(p.updatedAt).toLocaleDateString('fr-FR')}</span>
        {p.mine && <button title="Partager ce projet" onClick={(e) => { e.stopPropagation(); setSharing(p); }}>👤</button>}
        <button title="Exporter ce projet (.quickrouter.json autonome)" onClick={(e) => { e.stopPropagation(); exportProject(p); }}>⤓</button>
        {p.mine && (
          <button className="danger" onClick={(e) => {
            e.stopPropagation();
            if (window.confirm(`Supprimer le projet « ${p.name} » ?`)) deleteProjectById(p.id).catch((err) => setError(err.message));
          }}>✕</button>
        )}
      </div>
    );
  };

  const q = filter.trim().toLowerCase();
  const match = (p) => !q || p.name.toLowerCase().includes(q) || (p.owner ?? '').toLowerCase().includes(q);
  const board = BOARDS.find((b) => b.id === template);
  const num = (set) => (e) => set(Number(String(e.target.value).replace(',', '.')) || 0);

  return (
    <div className="picker home">
      <div className="home-head">
        {portal && <a className="suite-back" href={portal} title="Retour au portail Quick-Apps">← Quick-Apps</a>}
        <h1>Quick<span className="logo-sfx">·Router</span></h1>
        <span className="sub grow">Circuits imprimés assistés par Claude — placement, routage, DRC, Gerber</span>
        {me && <span className="suite-user">{me.username}{me.admin ? ' (admin)' : ''}</span>}
      </div>

      <div className="home-cols">
        <section className="home-new">
          <h2>Nouvelle carte</h2>
          <div className="step">1 · Carte mère</div>
          <div className="tiles">
            {BOARDS.map((b) => (
              <button key={b.id} className={`tile ${template === b.id ? 'active' : ''}`} onClick={() => setTemplate(b.id)}>
                <b>{b.name}</b><span>{b.sub}</span>
              </button>
            ))}
          </div>

          {template === 'ventuno-q' && (
            <>
              <div className="step">2 · Forme de la carte</div>
              <div className="tiles formes">
                {VQ_FORMES.map((f) => (
                  <button key={f.forme} className={`tile ${forme === f.forme ? 'active' : ''}`} onClick={() => setForme(f.forme)}>
                    <TemplatePreview id="ventuno-q" params={{ forme: f.forme }} height={92} />
                    <b>{f.name}</b><span>{f.sub}</span>
                  </button>
                ))}
              </div>
            </>
          )}

          <div className="new-summary">
            <TemplatePreview id={template} params={params} height={170} />
            <div className="new-form">
              <div className="step">{template === 'ventuno-q' ? '3' : '2'} · Réglages</div>
              <div className="muted">{board?.name}{template === 'ventuno-q' ? ` — ${VQ_FORMES[forme].name}` : ''}</div>
              {template === 'libre' && (
                <label className="row">Dimensions
                  <input className="num" value={dims.w} onChange={num((w) => setDims({ ...dims, w }))} /> ×
                  <input className="num" value={dims.h} onChange={num((h) => setDims({ ...dims, h }))} /> mm
                </label>
              )}
              {template === 'ventuno-q' && forme >= 3 && (
                <label className="row">Hauteur entre les cartes
                  <input className="num" value={empilage} onChange={num(setEmpilage)} /> mm
                </label>
              )}
              <input className="name" placeholder="Nom de la carte (ex. Shield RS485)" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && create()} />
              <div className="row">
                <button className="primary grow" disabled={!name.trim()} onClick={create}>Créer la carte</button>
                <button title="Importer un projet exporté (.quickrouter.json)" onClick={() => fileRef.current?.click()}>📥 Importer…</button>
              </div>
              <input ref={fileRef} type="file" accept=".json,application/json" hidden onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) importFile(f); }} />
            </div>
          </div>
        </section>

        <section className="home-list">
          <div className="row">
            <h2 className="grow">Mes cartes</h2>
            {projects.length > 4 && <input className="search" placeholder="Filtrer…" value={filter} onChange={(e) => setFilter(e.target.value)} />}
          </div>
          <div className="list">
            {mine.filter(match).map(row)}
            {!mine.length && <div className="empty">Aucune carte pour l’instant : choisissez une carte mère à gauche, donnez un nom, créez.</div>}
            {shared.length > 0 && <div className="list-title">Partagées avec moi</div>}
            {shared.filter(match).map(row)}
          </div>
          {busy && <div className="sub">{busy}</div>}
          {error && <div style={{ color: 'var(--danger)' }}>{error}</div>}
        </section>
      </div>
      {sharing && <ShareDialog project={sharing} onClose={() => setSharing(null)} />}
    </div>
  );
}
